/**
 * StorageDropdownActions - Componente de acciones para storages en prefacturas
 *
 * Dropdown con acciones disponibles para cada storage:
 * - Remover: Remover el storage de la prefactura
 */

import { faTrash, faEllipsisVertical } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Button } from '@shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { ViewPreInvoiceStorageTableRow } from '../../models/ViewPreInvoices';

interface StorageDropdownActionsProps {
  storage: ViewPreInvoiceStorageTableRow;
  onAction: (action: 'remove', storageId: number) => void;
}

export const StorageDropdownActions = ({ storage, onAction }: StorageDropdownActionsProps) => {
  const { t } = useTranslation('viewPreInvoicesModule');

  const hasStorageId = !!storage.storageId;

  const actions = [
    {
      id: 'remove',
      label: t('actions.remove_from_preinvoice', 'Remover de prefactura'),
      icon: faTrash,
      onClick: () => {
        if (hasStorageId && storage.storageId) {
          onAction('remove', storage.storageId);
        }
      },
      canShow: true,
      disabled: !hasStorageId,
      testId: 'remove-storage-action',
      variant: 'destructive' as const,
    },
  ].filter((action) => action.canShow);

  if (actions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`storage-actions-${storage.id}`}>
          <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4 text-icons" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.id}
            onClick={action.onClick}
            disabled={action.disabled}
            data-testid={action.testId}
            className={action.variant === 'destructive' ? 'text-destructive focus:text-destructive' : ''}
          >
            <FontAwesomeIcon icon={action.icon} className="mr-2 h-3.5 w-3.5 text-icons" />
            <span>{action.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
